/**
 * Trick Shot & Combo Scoring — detects cool plays, emits announcements,
 * tracks combo counters and style grades. Pure cosmetic — no mechanical effects.
 *
 * All detection is deterministic (derived from synced game state) — multiplayer-safe.
 */

import type { GameState, Projectile, TrickTag, TrickHit } from '../types';
import type { GameEffect } from './effects';
import {
  TRICK_TAGS, GRADE_THRESHOLDS, GRADE_COLORS, GRADE_FONT_SIZES,
  TRICK_DISTANCE, TRICK_WIND, TRICK_VOID, TRICK_SKY, TRICK_MOMENTUM, KNOCKBACK,
  MISS_TAUNTS, FUNNY_MISS_NAMES, TRICK_DISPLAY,
  LABEL_VARIANTS, HEADLINE_VARIANTS, WHIFF_VARIANTS, BOO_VARIANTS,
} from '../constants';

// ─── Variant picker ─────────────────────────────────────────────

/** Pick a text variant deterministically from an array. Seed should be state.frameCount. */
function pickVariant(variants: readonly string[], seed: number): string {
  if (variants.length === 0) return '';
  return variants[Math.abs(seed) % variants.length];
}

/** Pick a tag label variant. Falls back to the tag's base label if no variants exist. */
function pickTagLabel(baseLabel: string, seed: number): string {
  const variants = LABEL_VARIANTS[baseLabel];
  if (!variants || variants.length === 0) return baseLabel;
  return variants[Math.abs(seed) % variants.length];
}

// ─── Session lifecycle ──────────────────────────────────────────

/**
 * Capture fire origin. Called on transition to 'firing' phase.
 * Guards: skip void retaliation (owner -1) and re-entry (session already open).
 */
export function openTrickSession(state: GameState, _fx: GameEffect[]): void {
  if (state.currentPlayer < 0) return;
  if (state.trickSession) return; // void retaliation re-enters firing

  const p = state.players[state.currentPlayer];
  state.trickSession = {
    player: state.currentPlayer,
    fireX: p.x,
    fireY: p.y,
    wind: state.wind,
    fireGrounded: p.grounded,
    fireVx: p.vx,
    fireVy: p.vy,
    hits: [],
    combo: 0,
    totalBase: 0,
    allMultipliers: [],
  };
}

// ─── Per-hit recording ──────────────────────────────────────────

/**
 * Detect per-hit tricks, increment combo, emit trick tag announcements.
 * Called after each defaultExplosion that produces hits.
 */
export function recordTrickHit(
  state: GameState,
  proj: Projectile | null,
  targetIndex: number,
  x: number,
  y: number,
  damage: number,
  isDirect: boolean,
  fx: GameEffect[],
): void {
  const session = state.trickSession;
  if (!session) return;

  // Skip self-damage
  if (targetIndex === session.player) return;

  const tags: TrickTag[] = [];

  // ── Direct hit ──
  if (isDirect) {
    tags.push(TRICK_TAGS.DIRECT_HIT);
  }

  // ── Bounce tricks (skip when no projectile reference) ──
  const bouncesUsed = proj ? (proj.weapon.bounces ?? 0) - proj.bouncesRemaining : 0;
  if (bouncesUsed >= 3) {
    tags.push(TRICK_TAGS.TRICK_SHOT);
  } else if (bouncesUsed === 2) {
    tags.push(TRICK_TAGS.RICOCHET);
  } else if (bouncesUsed === 1) {
    tags.push(TRICK_TAGS.BANK_SHOT);
  }

  // ── Juggle (target airborne or tumbling) ──
  const target = state.players[targetIndex];
  if (target && (target.tumble || !target.grounded)) {
    tags.push(TRICK_TAGS.JUGGLE);
  }

  // ── Overkill ──
  if (damage >= 60) {
    tags.push(TRICK_TAGS.OVERKILL);
  }

  // ── Wind tricks (tiered by wind strength + direction) ──
  const absWind = Math.abs(session.wind);
  if (absWind > TRICK_WIND.MIN_WIND) {
    const dx = x - session.fireX;
    const isHeadwind = dx !== 0 && Math.sign(session.wind) !== Math.sign(dx);

    if (isHeadwind && absWind >= TRICK_WIND.HURRICANE_THRESHOLD) {
      // Near-max wind + headwind — top tier, replaces lower wind tags
      tags.push(TRICK_TAGS.HURRICANE);
    } else if (isHeadwind) {
      // Wind opposes shot direction
      tags.push(TRICK_TAGS.HEADWIND);
      // Strong headwind also gets GALE SHOT stacked
      if (absWind >= TRICK_WIND.GALE_THRESHOLD) {
        tags.push(TRICK_TAGS.GALE_SHOT);
      }
    } else if (absWind >= TRICK_WIND.GALE_THRESHOLD) {
      // Strong wind, but tailwind — still impressive to hit in a storm
      tags.push(TRICK_TAGS.TAILWIND);
    }
  }

  // ── Void proximity tricks ──
  // voidY is world-space Y of the void edge (Y increases downward).
  // Distance = voidY - playerY — positive when player is above void.
  const targetDistFromVoid = state.voidY - y;
  const attackerDistFromVoid = state.voidY - session.fireY;
  const targetNearVoid = targetDistFromVoid > 0 && targetDistFromVoid < TRICK_VOID.EDGE_DISTANCE;
  const attackerNearVoid = attackerDistFromVoid > 0 && attackerDistFromVoid < TRICK_VOID.ABYSS_DISTANCE;

  if (targetNearVoid && attackerNearVoid) {
    // Both near the void — top tier, replaces individual void tags
    tags.push(TRICK_TAGS.VOID_SNIPE);
  } else if (attackerNearVoid) {
    tags.push(TRICK_TAGS.ABYSS_SHOT);
  } else if (targetNearVoid) {
    tags.push(TRICK_TAGS.VOID_EDGE);
  }

  // ── Sky proximity tricks ──
  // Upper blast zone is negative Y (above world origin). Distance = playerY - blastZone.
  // Positive when player is below (safe side of) the blast zone.
  const skyZone = KNOCKBACK.UPPER_BLAST_ZONE;
  const targetDistFromSky = y - skyZone;
  const attackerDistFromSky = session.fireY - skyZone;
  const targetNearSky = targetDistFromSky > 0 && targetDistFromSky < TRICK_SKY.HIGH_DISTANCE;
  const attackerNearSky = attackerDistFromSky > 0 && attackerDistFromSky < TRICK_SKY.ORBIT_DISTANCE;

  if (targetNearSky && attackerNearSky) {
    tags.push(TRICK_TAGS.DEATH_WISH);
  } else if (attackerNearSky) {
    tags.push(TRICK_TAGS.ORBIT_SHOT);
  } else if (targetNearSky) {
    tags.push(TRICK_TAGS.SKY_HIGH);
  }

  // ── Airborne / momentum tricks ──
  const wasAirborne = !session.fireGrounded;
  const fireSpeed = Math.abs(session.fireVx);
  const dx = x - session.fireX;
  const movingAway = dx !== 0 && session.fireVx !== 0 && Math.sign(session.fireVx) !== Math.sign(dx);
  const movingToward = dx !== 0 && session.fireVx !== 0 && Math.sign(session.fireVx) === Math.sign(dx);

  if (wasAirborne && fireSpeed >= TRICK_MOMENTUM.FAST_SPEED) {
    // Airborne + fast — top tier, replaces AERIAL and directional tags
    tags.push(TRICK_TAGS.NO_SCOPE);
  } else {
    if (wasAirborne) {
      tags.push(TRICK_TAGS.AERIAL);
    }
    if (fireSpeed >= TRICK_MOMENTUM.MIN_SPEED) {
      if (movingAway) {
        tags.push(TRICK_TAGS.FADEAWAY);
      } else if (movingToward) {
        tags.push(TRICK_TAGS.FULL_SEND);
      }
    }
  }

  // Record hit
  const hit: TrickHit = {
    targetIndex,
    x,
    y,
    damage,
    isDirect,
    tags,
    bounceCount: proj ? bouncesUsed : 0,
  };
  session.hits.push(hit);
  session.combo++;
  session.totalBase += damage * 100;

  // Accumulate multipliers
  for (const tag of tags) {
    session.allMultipliers.push(tag.multiplier);
  }

  // Per-hit combo counter and trick tags suppressed during gameplay —
  // end-of-turn headline handles the payoff. Hits still recorded internally above.
}

// ─── Kill recording ─────────────────────────────────────────────

/**
 * Detect kill finisher tricks. Called from death.ts after matchStats tracking.
 */
export function recordTrickKill(
  state: GameState,
  playerIndex: number,
  deathType: 'void' | 'sky' | 'side',
  fx: GameEffect[],
): void {
  const session = state.trickSession;
  if (!session) return;

  // Only attribute kills to the session owner
  const victim = state.players[playerIndex];
  if (!victim) return;

  // Skip if victim is the session owner (self-kill)
  if (playerIndex === session.player) return;

  const attacker = state.players[session.player];
  if (!attacker) return;

  const killTags: TrickTag[] = [];

  // ── Void Dunk ──
  if (deathType === 'void') {
    killTags.push(TRICK_TAGS.VOID_DUNK);
  }

  // ── Launched (sky kill) ──
  if (deathType === 'sky') {
    killTags.push(TRICK_TAGS.LAUNCHED);
  }

  // ── Execution (victim had high damage) ──
  if (victim.damagePercent > 120) {
    killTags.push(TRICK_TAGS.EXECUTION);
  }

  // ── Revenge (victim was last person to hit attacker) ──
  if (attacker.lastHitBy === playerIndex) {
    killTags.push(TRICK_TAGS.REVENGE);
  }

  // ── Clutch (attacker at high damage) ──
  if (attacker.damagePercent > 80) {
    killTags.push(TRICK_TAGS.CLUTCH);
  }

  // Accumulate multipliers into session
  for (const tag of killTags) {
    session.allMultipliers.push(tag.multiplier);
  }

  // Emit kill tags
  const killX = victim.x;
  let killY = victim.y - 80;
  for (let ti = 0; ti < killTags.length; ti++) {
    const tag = killTags[ti];
    fx.push({
      type: 'announce_world',
      text: pickTagLabel(tag.label, state.frameCount + ti),
      x: killX,
      y: killY,
      color: tag.color,
      fontSize: TRICK_DISPLAY.KILL_FONT_SIZE,
      hold: TRICK_DISPLAY.KILL_HOLD,
    });
    killY -= 32;
  }
}

// ─── Edgeguard recording ────────────────────────────────────────

/**
 * Record edgeguard beam hit. Increments combo, adds EDGEGUARD tag.
 * Called from fireRailgunBeam() in phase-edgeguard.ts.
 */
export function recordEdgeguardHit(
  state: GameState,
  targetIndex: number,
  x: number,
  y: number,
  damage: number,
  fx: GameEffect[],
): void {
  const session = state.trickSession;
  if (!session) return;

  // Skip self-damage
  if (targetIndex === session.player) return;

  const tags: TrickTag[] = [TRICK_TAGS.EDGEGUARD];

  // ── Juggle (target airborne or tumbling — always true during edgeguard) ──
  const target = state.players[targetIndex];
  if (target && (target.tumble || !target.grounded)) {
    tags.push(TRICK_TAGS.JUGGLE);
  }

  // Record hit
  const hit: TrickHit = {
    targetIndex,
    x,
    y,
    damage,
    isDirect: true,
    tags,
    bounceCount: 0,
  };
  session.hits.push(hit);
  session.combo++;
  session.totalBase += damage * 100;

  // Accumulate multipliers
  for (const tag of tags) {
    session.allMultipliers.push(tag.multiplier);
  }

  // Combo counter suppressed — end-of-turn headline handles the payoff
}

// ─── Shot quality headline ──────────────────────────────────────

function getHeadline(combo: number, hasTrickTags: boolean, grade: string, seed: number): string {
  if (grade === 'S') return pickVariant(HEADLINE_VARIANTS.s_grade, seed);
  if (combo >= 5) return pickVariant(HEADLINE_VARIANTS.huge_combo, seed);
  if (combo >= 3) return pickVariant(HEADLINE_VARIANTS.nice_combo, seed);
  if (hasTrickTags) return pickVariant(HEADLINE_VARIANTS.sick_shot, seed);
  return pickVariant(HEADLINE_VARIANTS.nice_shot, seed);
}

// ─── Session close ──────────────────────────────────────────────

/**
 * Calculate distance tricks, compute final score + grade, handle miss streak,
 * emit staggered grade sequence, update cumulative scores + matchStats.
 * Called on entry to awaiting_next_turn phase.
 *
 * Sequence (each line delayed):
 *   1. Headline ("Nice Shot." / "Massive Combo." / miss taunt)
 *   2. Points ("+900 PTS")
 *   3. Grade ("B RANK")
 *   4. Flavor ("Not Bad." / "You're Elite.")
 */
export function closeTrickSession(state: GameState, fx: GameEffect[]): void {
  const session = state.trickSession;
  if (!session) return;

  const pi = session.player;
  state.trickSession = null;

  // ── Distance tricks (only if we had hits) ──
  if (session.hits.length > 0) {
    const stageWidth = state.stageRight - state.stageLeft;
    if (stageWidth > 0) {
      let maxDist = 0;
      for (const hit of session.hits) {
        const dx = hit.x - session.fireX;
        const dy = hit.y - session.fireY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxDist) maxDist = dist;
      }
      const distFraction = maxDist / stageWidth;

      if (distFraction > TRICK_DISTANCE.CROSS_MAP_FRACTION) {
        session.allMultipliers.push(TRICK_TAGS.CROSS_MAP.multiplier);
      } else if (distFraction > TRICK_DISTANCE.SNIPER_FRACTION) {
        session.allMultipliers.push(TRICK_TAGS.SNIPER.multiplier);
      }
    }
  }

  // ── Compute final score ──
  let totalScore = session.totalBase;
  for (const mult of session.allMultipliers) {
    totalScore *= mult;
  }
  totalScore = Math.round(totalScore);

  // ── Determine grade ──
  let grade: 'S' | 'A' | 'B' | 'C' | 'D';
  if (session.hits.length === 0) {
    grade = 'D';
  } else {
    grade = 'C'; // fallback
    for (const t of GRADE_THRESHOLDS) {
      if (totalScore >= t.minScore) {
        grade = t.grade;
        break;
      }
    }
  }

  const hold = TRICK_DISPLAY.SEQ_HOLD;

  const seed = state.frameCount;

  // ── Miss path ──
  if (session.hits.length === 0) {
    state.missStreak[pi]++;
    const streak = state.missStreak[pi];

    // Suppress miss taunts in tutorial
    if (state.modeState.mode !== 'tutorial') {
      if (streak >= 3) {
        const nameIdx = (streak - 3) % FUNNY_MISS_NAMES.length;
        fx.push({
          type: 'announce_screen',
          text: FUNNY_MISS_NAMES[nameIdx],
          color: MISS_TAUNTS.FUNNY_COLOR,
          fontSize: MISS_TAUNTS.FUNNY_FONT_SIZE,
          hold: 1.0,
        });
      } else if (streak === 2) {
        fx.push({
          type: 'announce_screen',
          text: pickVariant(BOO_VARIANTS, seed),
          color: MISS_TAUNTS.BOO_COLOR,
          fontSize: MISS_TAUNTS.BOO_FONT_SIZE,
          hold: 0.6,
        });
      } else {
        fx.push({
          type: 'announce_world',
          text: pickVariant(WHIFF_VARIANTS, seed),
          x: session.fireX,
          y: session.fireY - 80,
          color: MISS_TAUNTS.WHIFF_COLOR,
          fontSize: MISS_TAUNTS.WHIFF_FONT_SIZE,
          hold: 0.5,
        });
      }
    }
  } else {
    // ── Hit path — staggered sequence ──
    state.missStreak[pi] = 0;

    const gradeFs = GRADE_FONT_SIZES[grade];
    // Only show headline for B and above (C is silent)
    if (gradeFs > 0) {
      const hasTrickTags = session.allMultipliers.length > 0;

      // Headline only — flavor text IS the announcement. Points/grade suppressed.
      const headline = getHeadline(session.combo, hasTrickTags, grade, seed);
      fx.push({
        type: 'announce_screen',
        text: headline,
        color: GRADE_COLORS[grade],
        fontSize: 28,
        hold,
      });
    }
  }

  // ── Update cumulative state ──
  state.trickScores[pi] += totalScore;

  // ── Update matchStats ──
  if (state.matchStats) {
    if (session.combo > state.matchStats.bestCombo[pi]) {
      state.matchStats.bestCombo[pi] = session.combo;
    }
    if (totalScore > state.matchStats.bestTurnScore[pi]) {
      state.matchStats.bestTurnScore[pi] = totalScore;
    }
    state.matchStats.totalTrickScore[pi] += totalScore;
  }
}
